import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus } from "@phosphor-icons/react";
import MeetingModeTabs from "../components/meetings/MeetingModeTabs";
import MeetingCard from "../components/meetings/MeetingCard";
import { meetingService } from "../services/meetingService";
import { placeService } from "../services/placeService";
import { authService } from "../services/authService";
import { accountService } from "../services/accountService";
import { useGeolocation } from "../hooks/useGeolocation";

export default function MeetingsPage() {
  const navigate = useNavigate();
  const me = authService.getCurrentUser();
  const { position } = useGeolocation();
  const [mode, setMode] = useState("organize"); // "organize" | "participate"
  const [organized, setOrganized] = useState([]);
  const [compatible, setCompatible] = useState([]);
  const [participations, setParticipations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      let lat = position?.lat;
      let lng = position?.lng;
      if (typeof lat !== "number" || typeof lng !== "number") {
        const saved = await accountService.getSavedLocation();
        lat = saved?.lat;
        lng = saved?.lng;
      }

      let places = [];
      if (typeof lat === "number" && typeof lng === "number") {
        places = await placeService
          .listNearby({ latitude: lat, longitude: lng, radiusKm: 100, forMap: true })
          .catch(() => []);
      }

      if (mode === "organize") {
        const list = await meetingService.listByOrganizer(me?.id);
        setOrganized(meetingService.enrichWithPlaces(list, places));
      } else {
        const [list, parts] = await Promise.all([
          meetingService.listCompatible({ profile: null, lat, lng, radiusKm: 100 }),
          meetingService.myParticipations(),
        ]);
        setCompatible(meetingService.enrichWithPlaces(list, places));
        setParticipations(parts);
      }
      setLoading(false);
    })();
  }, [mode, me?.id, position?.lat, position?.lng]);

  const myJoinedIds = new Set(participations.map((p) => p.meetingId));

  return (
    <div className="px-5 pt-6" data-testid="meetings-page">
      <header className="mx-auto mb-6 flex max-w-3xl items-end justify-between">
        <div>
          <p className="zx-label">Tu agenda secreta</p>
          <h1 className="font-serif text-3xl tracking-tight md:text-4xl">Encuentros</h1>
        </div>
        <button
          onClick={() => navigate("/meetings/new")}
          className="flex items-center gap-2 rounded-full border border-[rgba(184,50,90,0.45)] bg-[rgba(139,30,63,0.18)] px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-[#e9b5c5] hover:bg-[rgba(139,30,63,0.32)]"
          data-testid="meetings-create-button"
        >
          <Plus size={14} weight="thin" /> Crear
        </button>
      </header>

      <div className="mx-auto max-w-3xl">
        <MeetingModeTabs value={mode} onChange={setMode} />

        {loading ? (
          <p className="mt-10 text-center text-xs uppercase tracking-[0.2em] text-[rgba(255,255,255,0.45)]">
            Cargando...
          </p>
        ) : mode === "organize" ? (
          <div className="mt-6 space-y-4 zx-stagger" data-testid="organize-list">
            {organized.length === 0 ? (
              <EmptyState
                title="Aún no organizas nada"
                body="Crea tu primer encuentro y deja una pista discreta para reconocerse."
                cta="Organizar encuentro"
                onCta={() => navigate("/meetings/new")}
              />
            ) : (
              organized.map((m) => (
                <MeetingCard key={m.id} meeting={m} mine onClick={() => navigate(`/meetings/${m.id}`)} />
              ))
            )}
          </div>
        ) : (
          <div className="mt-6 space-y-4 zx-stagger" data-testid="participate-list">
            {compatible.length === 0 ? (
              <EmptyState
                title="No hay encuentros compatibles"
                body="Amplía tu radio o vuelve a la noche."
                cta="Ver mapa"
                onCta={() => navigate("/map")}
              />
            ) : (
              compatible.map((m) => (
                <MeetingCard
                  key={m.id}
                  meeting={m}
                  joined={myJoinedIds.has(m.id)}
                  onClick={() => navigate(`/meetings/${m.id}`)}
                />
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function EmptyState({ title, body, cta, onCta }) {
  return (
    <div className="zx-card flex flex-col items-center gap-3 p-8 text-center">
      <p className="zx-label">vacío</p>
      <h3 className="font-serif text-2xl">{title}</h3>
      <p className="text-sm text-[rgba(255,255,255,0.6)]">{body}</p>
      <button
        onClick={onCta}
        className="mt-2 rounded-full bg-[#8B1E3F] px-5 py-2 text-[10px] uppercase tracking-[0.22em] text-[#f5f5f5] hover:bg-[#B8325A]"
        data-testid="empty-state-cta"
      >
        {cta}
      </button>
    </div>
  );
}
